import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { FaChevronDown, FaChevronUp, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api';
import DatePickerField from '../common/DatePickerField';
import Alert from '../common/Alert';
import Table from '../common/Table';
import 'animate.css';
import { formatIstDateTime } from '../../utils/dateTime';
import CommunityIdentity from '../common/CommunityIdentity';

/**
 * Component to view audit logs with filtering and pagination.
 * Displays logs in a table for desktop and cards for mobile.
 */
const AuditLogViewer = () => {
  const { session } = useContext(AuthContext);
  const { communityId, roles } = session || {};
  const isSuperAdmin = roles?.includes('SUPER_ADMIN') || false;

  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    userId: '',
    resource: '',
    startDate: null,
    endDate: null,
    communityId: isSuperAdmin ? '' : undefined,
  });
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 1,
    totalElements: 0,
  });
  const [resources, setResources] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const debounceRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchCommunitiesForSuperAdmin = async () => {
      if (!isSuperAdmin || !isMounted.current) return;
      setIsLoadingCommunities(true);
      try {
        const response = await api.get('/api/communities/active');
        const communityList = Array.isArray(response.data) ? response.data : response.data?.data || [];
        if (isMounted.current) {
          setCommunities(communityList);
          setError('');
        }
      } catch (err) {
        if (isMounted.current) {
          setError('Failed to load communities. Please try again.');
          toast.error('Failed to load communities.');
        }
      } finally {
        if (isMounted.current) {
          setIsLoadingCommunities(false);
        }
      }
    };

    fetchCommunitiesForSuperAdmin();
  }, [isSuperAdmin]);

  const fetchLogs = useCallback(async () => {
    if (isFetching || !isMounted.current) return;
    setIsFetching(true);
    try {
      const params = {
        userId: filters.userId || undefined,
        resource: filters.resource || undefined,
        startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
        communityId: filters.communityId || undefined,
        page: pagination.page,
        size: pagination.size,
      };
      const response = await api.get('/api/audit', { params });
      if (isMounted.current) {
        const fetchedLogs = response.data.content || [];
        setLogs(fetchedLogs);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.totalPages || 1,
          totalElements: response.data.totalElements || 0,
        }));
        setResources([...new Set(fetchedLogs.map(log => log.resource))]);
        setError('');
      }
    } catch (err) {
      if (isMounted.current) {
        const errorMessage = err.response?.data?.message || 'An error occurred while fetching logs.';
        setError(errorMessage);
        toast.error('Failed to fetch logs. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsFetching(false);
      }
    }
  }, [
    filters.userId,
    filters.resource,
    filters.startDate,
    filters.endDate,
    filters.communityId,
    pagination.page,
    pagination.size,
  ]);

  const debouncedFetchLogs = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (isMounted.current) {
        fetchLogs();
      }
    }, 300);
  }, [fetchLogs]);

  useEffect(() => {
    debouncedFetchLogs();
  }, [filters, pagination.page, pagination.size, debouncedFetchLogs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleDateChange = (name, date) => {
    setFilters(prev => ({ ...prev, [name]: date }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    debouncedFetchLogs();
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      resource: '',
      startDate: null,
      endDate: null,
      communityId: isSuperAdmin ? '' : undefined,
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const download = async format => {
    try {
      const params = {
        userId: filters.userId || undefined,
        resource: filters.resource || undefined,
        startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
        communityId: filters.communityId || undefined,
      };
      const response = await api.get(`/api/audit/export/${format}`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || `Unable to export ${format.toUpperCase()} audit report.`);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const toggleExpand = (id) => {
    setExpandedLog(expandedLog === id ? null : id);
  };

  const columns = isSuperAdmin
    ? ['User ID', 'Action', 'Resource', 'Community', 'Timestamp (IST)', 'Details']
    : ['User ID', 'Action', 'Resource', 'Timestamp (IST)', 'Details'];

  const renderRow = (log) => {
    const diff = computeDiff(log.oldData, log.newData);
    return (
      <tr key={log.id}>
        <td>{log.userId}</td>
        <td>{log.action}</td>
        <td>{log.resource}</td>
        {isSuperAdmin && (
          <td>{log.community ? <CommunityIdentity community={log.community} size="xs" compact /> : 'N/A'}</td>
        )}
        <td>{formatIstDateTime(log.timestamp)}</td>
        <td>
          {log.oldData && (
            <details>
              <summary>Old Data</summary>
              <pre>{renderData(log.oldData)}</pre>
            </details>
          )}
          {log.newData && (
            <details>
              <summary>New Data</summary>
              <pre>{renderData(log.newData)}</pre>
            </details>
          )}
          <details>
            <summary>Changes</summary>
            {renderDiff(diff)}
          </details>
        </td>
      </tr>
    );
  };

  const renderMobileCard = (log) => {
    const diff = computeDiff(log.oldData, log.newData);
    const isExpanded = expandedLog === log.id;
    return (
      <div
        key={log.id}
        className="card shadow-sm mb-3 animate__animated animate__fadeInUp"
      >
        <div
          className="card-header d-flex justify-content-between align-items-center bg-light"
          onClick={() => toggleExpand(log.id)}
          style={{ cursor: 'pointer' }}
        >
          <h6 className="mb-0">
            {log.userId} - {log.action}
          </h6>
          {isExpanded ? (
            <FaChevronUp size={16} className="text-muted" />
          ) : (
            <FaChevronDown size={16} className="text-muted" />
          )}
        </div>
        <div className="card-body">
          <p className="mb-1">
            <strong>Timestamp (IST):</strong> {formatIstDateTime(log.timestamp)}
          </p>
          {isExpanded && (
            <>
              <p className="mb-1">
                <strong>Resource:</strong> {log.resource || '-'}
              </p>
              {isSuperAdmin && (
                <p className="mb-1">
                  <strong>Community:</strong>{' '}
                  {log.community ? <CommunityIdentity community={log.community} size="xs" compact /> : 'N/A'}
                </p>
              )}
              <div className="mt-2">
                {log.oldData && (
                  <details>
                    <summary>Old Data</summary>
                    <pre className="bg-light p-2 rounded">{renderData(log.oldData)}</pre>
                  </details>
                )}
                {log.newData && (
                  <details>
                    <summary>New Data</summary>
                    <pre className="bg-light p-2 rounded">{renderData(log.newData)}</pre>
                  </details>
                )}
                <details>
                  <summary>Changes</summary>
                  <div className="bg-light p-2 rounded">{renderDiff(diff)}</div>
                </details>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container py-3 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-4 flex-wrap">
        <h2 className="fs-4 fw-bold mb-0">Audit Trail</h2>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={() => download('csv')}><FaDownload /> CSV</button>
          <button type="button" className="btn btn-outline-primary" onClick={() => download('pdf')}><FaDownload /> PDF</button>
        </div>
      </div>
      <div className="card shadow mb-4">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3">Filter Logs</h5>
          <div className="d-flex flex-wrap gap-3">
            <div className="form-group flex-fill">
              <label htmlFor="userId">User ID</label>
              <input
                type="text"
                className="form-control"
                id="userId"
                name="userId"
                value={filters.userId}
                onChange={handleFilterChange}
                placeholder="Enter user ID"
              />
            </div>
            <div className="form-group flex-fill">
              <label htmlFor="resource">Resource</label>
              <select
                className="form-control"
                id="resource"
                name="resource"
                value={filters.resource}
                onChange={handleFilterChange}
              >
                <option value="">All Resources</option>
                {resources.map(res => (
                  <option key={res} value={res}>{res}</option>
                ))}
              </select>
            </div>
            {isSuperAdmin && (
              <div className="form-group flex-fill">
                <label htmlFor="communityId">Community</label>
                {isLoadingCommunities ? (
                  <div>Loading communities...</div>
                ) : communities.length === 0 ? (
                  <div>No communities available</div>
                ) : (
                  <select
                    className="form-control"
                    id="communityId"
                    name="communityId"
                    value={filters.communityId}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Communities</option>
                    {communities.map(community => (
                      <option key={community.id} value={community.id}>
                        {community.displayName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <div className="form-group flex-fill">
              <label htmlFor="startDate">Start Date</label>
              <DatePickerField
                id="startDate"
                selected={filters.startDate}
                onChange={date => handleDateChange('startDate', date)}
                placeholder="Select start date"
              />
            </div>
            <div className="form-group flex-fill">
              <label htmlFor="endDate">End Date</label>
              <DatePickerField
                id="endDate"
                selected={filters.endDate}
                onChange={date => handleDateChange('endDate', date)}
                placeholder="Select end date"
              />
            </div>
            <div className="form-group d-flex align-items-end gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyFilters}
                disabled={isFetching}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={clearFilters}
                disabled={isFetching}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
      {error && <Alert type="danger" message={error} />}
      {logs.length === 0 ? (
        <Alert type="info" message="No audit logs found." />
      ) : (
        <>
          {/* Desktop Table */}
          <div className="card shadow mx-auto d-none d-md-block" style={{ maxWidth: '1200px' }}>
            <div className="card-body">
              <h5 className="card-title fs-5 mb-3">Audit Logs</h5>
              <Table columns={columns} data={logs} renderRow={renderRow} />
            </div>
          </div>
          {/* Mobile Cards */}
          <div className="d-md-none">
            {logs.map(renderMobileCard)}
          </div>
          {/* Pagination */}
          <nav>
            <ul className="pagination justify-content-center flex-wrap">
              <li className={`page-item ${pagination.page === 0 ? 'disabled' : ''}`}>
                <button
                  type="button"
                  className="page-link"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 0}
                >
                  Previous
                </button>
              </li>
              {[...Array(Math.min(pagination.totalPages, 5)).keys()].map(i => {
                const pageNum = i + Math.max(0, pagination.page - 2);
                if (pageNum >= pagination.totalPages) return null;
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                  >
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum + 1}
                    </button>
                  </li>
                );
              })}
              <li
                className={`page-item ${
                  pagination.page >= pagination.totalPages - 1 ? 'disabled' : ''
                }`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages - 1}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
          <p className="text-center">
            Showing {logs.length} of {pagination.totalElements} logs
          </p>
        </>
      )}
    </div>
  );
};

/**
 * Computes the difference between old and new data.
 *
 * @param {string} oldData - The previous state of the data
 * @param {string} newData - The new state of the data
 * @returns {Object|null} - The differences between old and new data, or null if no differences
 */
const computeDiff = (oldData, newData) => {
  if (!oldData || !newData) return null;
  try {
    const oldObj = JSON.parse(oldData);
    const newObj = JSON.parse(newData);
    const diff = {};
    for (const key of Object.keys({ ...oldObj, ...newObj })) {
      if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
        diff[key] = { old: oldObj[key], new: newObj[key] };
      }
    }
    return Object.keys(diff).length > 0 ? diff : null;
  } catch {
    return null;
  }
};

/**
 * Renders data in a readable format.
 *
 * @param {string} data - The data to render
 * @returns {string} - The formatted data
 */
const renderData = (data) => {
  if (!data) return 'N/A';
  try {
    return JSON.stringify(JSON.parse(data), null, 2);
  } catch {
    return data;
  }
};

/**
 * Renders the differences between old and new data.
 *
 * @param {Object} diff - The differences to render
 * @returns {JSX.Element} - The rendered differences
 */
const renderDiff = (diff) => {
  if (!diff) return <span>No changes detected</span>;
  return (
    <ul style={{ listStyle: 'none', padding: 0 }}>
      {Object.entries(diff).map(([key, { old, new: newValue }]) => (
        <li key={key}>
          <strong>{key}:</strong>
          <span style={{ color: 'red', textDecoration: 'line-through', marginLeft: '5px' }}>
            {JSON.stringify(old ?? 'null')}
          </span>
          {' → '}
          <span style={{ color: 'green' }}>
            {JSON.stringify(newValue ?? 'null')}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default AuditLogViewer;
